import { createContext } from 'react'
import { BrandConfig, EnvConfig } from 'src/configurations'
import { ExtendLocales } from 'src/enums'
// Type-only import — erased at compile time, so this file stays free of any
// runtime Redux dependency. That matters: the localization layer
// (useTranslate, translateNode) imports the context object from here, and
// models/validators import the localization layer. If this file pulled in the
// reducer graph at runtime it would recreate the circular-import chain
// (models → localization → context → reducers → selectors → models).
// The legacy config reducer was deleted in the React Query migration; its
// ConfigState was field-identical to ConfigResponse, so AppConfigState is now
// an alias of the API response model (still a pure type module, no redux).
import type { ConfigResponse } from 'src/models/config.models'

export type AppConfigState = ConfigResponse

type BaseRequestHeaders = {
  headers: {
    'X-Extend-Brand': string
    'X-Extend-App-ID': string
    'X-Extend-Platform': string
    'X-Extend-Platform-Version': string
    Accept: string
    'Content-Type': string
  }
  baseURL: string
}

export type { BaseRequestHeaders }

export type ConfigContextActions = {
  /** Refreshes application configuration */
  refreshConfigContext: () => Promise<void>
  setLocale: (locale: ExtendLocales) => void
  getBaseRequestHeaders: () => BaseRequestHeaders
}

export type ConfigContextState = {
  environment: EnvConfig
  brand: BrandConfig
  locale: ExtendLocales
  appConfig: AppConfigState
}

export type ConfigContextValue = ConfigContextActions & ConfigContextState

const stubConfigContextActions: ConfigContextActions = {
  refreshConfigContext: () => {
    throw Error('ConfigContext Not Available')
  },
  setLocale: () => {
    throw Error('ConfigContext Not Available')
  },
  getBaseRequestHeaders: () => {
    throw Error('ConfigContext Not Available')
  },
}

// Empty initial config — the localization layer renders against this until the
// React Query session-config fetch resolves. Exported so call sites that seed
// the provider (src/client.tsx) share one source of truth.
export const initialAppConfigState: AppConfigState = {
  departmentOptions: [],
  departmentOptionsLocalized: [],
  supportedLocales: [],
  supportedCurrencies: [],
  supportedIndustries: [],
  supportedIndustriesLocalized: [],
  teamSizeOptions: [],
  referralSourceOptions: [],
  referralSourceOptionsLocalized: [],
}

export const defaultConfigContextState: ConfigContextState = {
  environment:
    typeof document !== 'undefined'
      ? JSON.parse(document.getElementById('__APP_CONFIG__')?.innerHTML || '{}')
      : ({} as EnvConfig),
  brand:
    typeof document !== 'undefined'
      ? JSON.parse(
          document.getElementById('__BRAND_CONFIG__')?.innerHTML || '{}',
        )
      : ({} as BrandConfig),
  locale:
    typeof window !== 'undefined'
      ? (window.navigator.language as ExtendLocales)
      : ('en-US' as ExtendLocales),
  appConfig: initialAppConfigState,
}

export const ConfigContext = createContext<ConfigContextValue>({
  ...defaultConfigContextState,
  ...stubConfigContextActions,
})
